"use client";

import { useState, useEffect, useMemo } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { patch, fetchData } from "@/utils/api-handler";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import { toast } from "sonner";
import { FormField } from "@/components/shared/form-fields";
import { Loader } from "@/components/ui/loader";
import { VisuallyHidden } from "@/components/ui/visually-hidden";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "@/components/ui/card";
import { TEXTAREA_MIN_HEIGHT } from "@/utils/global";
import { CommandAgent } from "@/types/command-agent";
import { CommandAgentSharingCard } from "@/components/command-team/command-agent/cards/command-agent-sharing-card";
import { isSystemCommandAgent, sameRoleIdLists } from "@/components/command-team/command-agent/utils/command-agent-sharing-utils";
import { applyLlmProviderChange } from "@/components/command-team/command-agent/utils/command-agent-llm-utils";

interface RolesListResponse {
    items?: Array<{ id?: string; title?: string }>;
}

interface EditCommandAgentProps {
    agent: CommandAgent | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface LLMModel {
    provider: string;
    model_key: string;
    model_id: string;
    display_name: string;
    context_window: number;
    default_max_tokens: number;
    max_tokens_min: number;
    max_tokens_max: number;
    default_temperature: number;
    temperature_min: number;
    temperature_max: number;
    supports_streaming: boolean;
    supports_tools: boolean;
}

interface LLMModelsResponse {
    models: LLMModel[];
    total: number;
}

export function EditCommandAgent({ agent, open, onOpenChange }: EditCommandAgentProps) {
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        role: "",
        persona: "",
        restrictions: "",
        llm_provider: "",
        llm_model: "",
        visibility: "PERSONAL" as AppIntegrationVisibility,
        shared_roles: [] as string[],
    });

    const [errors, setErrors] = useState({
        name: "",
        description: "",
        role: "",
        persona: "",
        restrictions: "",
        llm_provider: "",
        llm_model: "",
        visibility: "",
        shared_roles: "",
    });

    // Fetch LLM models
    const { data: llmModelsData, isLoading: isLoadingModels } = useQuery<LLMModelsResponse>({
        queryKey: ["llm-models"],
        queryFn: () => fetchData("/api/command-team/llm-config/models"),
        enabled: open,
    });

    const { data: agentDetail } = useProtectedQuery<CommandAgent>({
        queryKey: ["command-agent", agent?.id, "edit-dialog"],
        queryFn: () => fetchData(`/api/command-team/agents/${agent!.id}`),
        enabled: open && Boolean(agent?.id),
        requiredPermissions: ["VIEW_COMMAND_AGENT"],
    });

    const agentForForm = agentDetail ?? agent;
    const isSystemAgent = agentForForm ? isSystemCommandAgent(agentForForm) : false;

    const { data: rolesQueryData, isLoading: rolesLoading, hasPermission: canListRoles } =
        useProtectedQuery<RolesListResponse | Array<{ id?: string; title?: string }>>({
            queryKey: ["command-agent-role-options-edit"],
            requiredPermissions: ["LIST_ROLES"],
            queryFn: async () => {
                const params = new URLSearchParams({ page: "1", limit: "500" });
                return fetchData(`/api/rbac/roles?${params.toString()}`);
            },
            enabled: open,
        });

    const roleOptions = useMemo(() => {
        const raw = rolesQueryData;
        const items = Array.isArray(raw) ? raw : raw?.items ?? [];
        return items
            .filter((r): r is { id: string; title?: string } => Boolean(r.id))
            .map((r) => ({ label: r.title?.trim() || r.id, value: r.id }));
    }, [rolesQueryData]);

    // Get models filtered by provider
    const filteredModels = useMemo(() => {
        if (!llmModelsData?.models) return [];
        if (!formData.llm_provider) return [];
        return llmModelsData.models.filter((model) => model.provider === formData.llm_provider);
    }, [llmModelsData?.models, formData.llm_provider]);

    // Initialize form when agent changes or dialog opens (prefer full agent from API for visibility fields)
    useEffect(() => {
        if (agentForForm && open) {
            const agentModel = llmModelsData?.models?.find(
                (model) => model.model_id === agentForForm.llm_model || model.model_key === agentForForm.llm_model
            );
            const provider = agentForForm.llm_provider || agentModel?.provider || "";
            const normalizedModelKey = agentModel?.model_key || agentForForm.llm_model || "";

            setFormData({
                name: agentForForm.name || "",
                description: agentForForm.description || "",
                role: agentForForm.role || "",
                persona: agentForForm.persona || "",
                restrictions: agentForForm.restrictions || "",
                llm_provider: provider,
                llm_model: normalizedModelKey,
                visibility: agentForForm.visibility ?? "PERSONAL",
                shared_roles: [...(agentForForm.shared_roles ?? [])],
            });
            setErrors({
                name: "",
                description: "",
                role: "",
                persona: "",
                restrictions: "",
                llm_provider: "",
                llm_model: "",
                visibility: "",
                shared_roles: "",
            });
        }
    }, [agentForForm, open, llmModelsData]);

    const updateMutation = useProtectedMutation({
        mutationFn: (data: Record<string, any>) => {
            return patch(`/api/command-team/agents/${agent!.id}`, data);
        },
        requiredPermissions: ["UPDATE_COMMAND_AGENT"],
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["command-agents"] });
            queryClient.invalidateQueries({ queryKey: ["command-agents-sidebar"] });
            queryClient.invalidateQueries({ queryKey: ["command-agent", agent?.id] });
            toast.success("Command Agent updated successfully");
            onOpenChange(false);
        },
        onError: (error: any) => {
            const newErrors = {
                name: "",
                description: "",
                role: "",
                persona: "",
                restrictions: "",
                llm_provider: "",
                llm_model: "",
                visibility: "",
                shared_roles: "",
            };

            if (error.response?.data?.detail) {
                const validationErrors = error.response.data.detail;
                validationErrors.forEach((err: any) => {
                    const field = err.loc[1];
                    const message = err.msg;
                    if (field in newErrors) {
                        newErrors[field as keyof typeof newErrors] = message;
                    }
                });
                setErrors(newErrors);
            } else {
                toast.error("Failed to update command agent", {
                    description: error.response?.data?.message || "An unexpected error occurred",
                });
            }
        },
    });

    const handleInputChange = (field: string, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        if (errors[field as keyof typeof errors]) {
            setErrors((prev) => ({
                ...prev,
                [field]: "",
            }));
        }
    };

    const handleProviderChange = (provider: string) => {
        setFormData((prev) => applyLlmProviderChange(prev, provider, llmModelsData?.models));
        if (errors.llm_provider) {
            setErrors((prev) => ({ ...prev, llm_provider: "" }));
        }
        if (errors.llm_model) {
            setErrors((prev) => ({ ...prev, llm_model: "" }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!agent || !agentForForm) return;

        const submitData: Record<string, any> = {};

        // Only send changed fields (baseline prefers full agent from API when loaded)
        if (formData.name !== (agentForForm.name || "")) submitData.name = formData.name;
        if (formData.role !== (agentForForm.role || "")) submitData.role = formData.role;
        if (formData.persona !== (agentForForm.persona || "")) submitData.persona = formData.persona;

        const desc = formData.description.trim() === "" ? null : formData.description;
        if (desc !== (agentForForm.description || null)) submitData.description = desc;

        const rest = formData.restrictions.trim() === "" ? null : formData.restrictions;
        if (rest !== (agentForForm.restrictions || null)) submitData.restrictions = rest;

        if (formData.llm_provider && formData.llm_provider !== (agentForForm.llm_provider || "")) {
            submitData.llm_provider = formData.llm_provider;
        }
        if (formData.llm_model && formData.llm_model !== (agentForForm.llm_model || "")) {
            submitData.llm_model = formData.llm_model;
        }

        if (!isSystemAgent) {
            const agentVisibility = agentForForm.visibility ?? "PERSONAL";
            const agentShared = agentForForm.shared_roles ?? [];
            const formShared = formData.visibility === "TEAM" ? formData.shared_roles : [];

            if (formData.visibility !== agentVisibility) {
                submitData.visibility = formData.visibility;
            }
            if (!sameRoleIdLists(formShared, agentShared)) {
                submitData.shared_roles = formShared;
            }
        }

        if (Object.keys(submitData).length === 0) {
            toast.info("No changes to save");
            return;
        }

        updateMutation.mutate(submitData);
    };

    const handleCancel = () => {
        setErrors({
            name: "",
            description: "",
            role: "",
            persona: "",
            restrictions: "",
            llm_provider: "",
            llm_model: "",
            visibility: "",
            shared_roles: "",
        });
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-full p-0 max-w-[830px] [&>button:last-child]:hidden max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <VisuallyHidden>
                        <DialogTitle>Edit Command Agent</DialogTitle>
                    </VisuallyHidden>
                </DialogHeader>
                <div className="sticky top-0 z-50 bg-background border-b px-4 sm:px-6 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="text-center sm:text-left">
                            <h2 className="sm:text-xl font-semibold">Edit Command Agent</h2>
                            <p className="text-sm text-muted-foreground">
                                Update your command agent settings.
                            </p>
                        </div>
                        <div className="flex flex-col-reverse sm:flex-row gap-3 w-full sm:w-auto">
                            <Button
                                variant="outline"
                                onClick={handleCancel}
                                className="w-full sm:w-auto"
                            >
                                Cancel
                            </Button>
                            <Button
                                className="bg-primary hover:bg-primary/90 w-full sm:w-auto"
                                onClick={handleSubmit}
                                disabled={updateMutation.isPending}
                            >
                                {updateMutation.isPending ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-2" />
                                        <span>Save Changes</span>
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>

                    {updateMutation.isPending && <Loader />}
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="p-3 space-y-3">
                        <Card className="shadow-sm border-border/40">
                            <CardHeader className="pb-2 px-4 sm:px-6">
                                <CardTitle className="text-base font-medium">
                                    General
                                </CardTitle>
                                <CardDescription>
                                    Basic agent configuration
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="px-4 sm:px-6 space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField
                                        label="Agent Name"
                                        error={errors.name}
                                        description="Enter a unique name for your command agent"
                                    >
                                        <Input
                                            value={formData.name}
                                            onChange={(e) => handleInputChange("name", e.target.value)}
                                            placeholder="e.g., Customer Support Agent"
                                            className={`border-border/40 ${errors.name ? "border-destructive" : ""}`}
                                        />
                                    </FormField>

                                    <FormField
                                        label="Role"
                                        error={errors.role}
                                        description="Specify the role of this agent"
                                    >
                                        <Input
                                            value={formData.role}
                                            onChange={(e) => handleInputChange("role", e.target.value)}
                                            placeholder="e.g., Customer Support Specialist"
                                            className={`border-border/40 ${errors.role ? "border-destructive" : ""}`}
                                        />
                                    </FormField>
                                </div>

                                <FormField
                                    label="Description"
                                    error={errors.description}
                                    description="Brief description of the agent's purpose"
                                >
                                    <Textarea
                                        value={formData.description}
                                        onChange={(e) => handleInputChange("description", e.target.value)}
                                        placeholder="Command agent for customer support tasks."
                                        className={`${TEXTAREA_MIN_HEIGHT} ${errors.description ? "border-destructive" : ""}`}
                                    />
                                </FormField>
                            </CardContent>
                        </Card>

                        {!isSystemAgent ? (
                        <CommandAgentSharingCard
                            idPrefix="edit-command-agent"
                            visibility={formData.visibility}
                            sharedRoles={formData.shared_roles}
                            canListRoles={canListRoles}
                            rolesLoading={rolesLoading}
                            roleOptions={roleOptions}
                            errors={{
                                visibility: errors.visibility,
                                shared_roles: errors.shared_roles,
                            }}
                            onVisibilityChange={(visibility) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    visibility,
                                    shared_roles: visibility === "TEAM" ? prev.shared_roles : [],
                                }));
                                setErrors((prev) => ({
                                    ...prev,
                                    visibility: "",
                                    shared_roles: "",
                                }));
                            }}
                            onSharedRolesChange={(value) => {
                                setFormData((prev) => ({ ...prev, shared_roles: value }));
                                setErrors((prev) => ({ ...prev, shared_roles: "" }));
                            }}
                        />
                        ) : null}

                        <Card className="shadow-sm border-border/40">
                            <CardHeader className="pb-2 px-4 sm:px-6">
                                <CardTitle className="text-base font-medium">
                                    Behavior
                                </CardTitle>
                                <CardDescription>
                                    Define the agent&apos;s persona and restrictions
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="px-4 sm:px-6 space-y-6">
                                <FormField
                                    label="Persona"
                                    error={errors.persona}
                                    description="Define the agent's personality and expertise"
                                >
                                    <Textarea
                                        value={formData.persona}
                                        onChange={(e) => handleInputChange("persona", e.target.value)}
                                        placeholder="An expert in handling customer support queries and providing helpful responses."
                                        className={`${TEXTAREA_MIN_HEIGHT} ${errors.persona ? "border-destructive" : ""}`}
                                    />
                                </FormField>

                                <FormField
                                    label="Restrictions"
                                    error={errors.restrictions}
                                    description="Specify what the agent should not do or avoid"
                                >
                                    <Textarea
                                        value={formData.restrictions}
                                        onChange={(e) => handleInputChange("restrictions", e.target.value)}
                                        placeholder="Do not provide financial advice or make promises."
                                        className={`${TEXTAREA_MIN_HEIGHT} ${errors.restrictions ? "border-destructive" : ""}`}
                                    />
                                </FormField>
                            </CardContent>
                        </Card>

                        <Card className="shadow-sm border-border/40">
                            <CardHeader className="pb-2 px-4 sm:px-6">
                                <CardTitle className="text-base font-medium">
                                    AI Configuration
                                </CardTitle>
                                <CardDescription>
                                    Select the AI provider and model
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="px-4 sm:px-6 space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField
                                        label="LLM Provider"
                                        error={errors.llm_provider}
                                        description="Select the AI provider"
                                    >
                                        <Select
                                            value={formData.llm_provider || "OPENAI"}
                                            onValueChange={handleProviderChange}
                                            key={`provider-${formData.llm_provider}`}
                                        >
                                            <SelectTrigger className={`border-border/40 ${errors.llm_provider ? "border-destructive" : ""}`}>
                                                <SelectValue placeholder="Select a provider" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="OPENAI">OpenAI</SelectItem>
                                                <SelectItem value="ANTHROPIC">Anthropic</SelectItem>
                                                <SelectItem value="MOONSHOTAI">MoonshotAI</SelectItem>
                                                <SelectItem value="ZHIPU">Zhipu</SelectItem>
                                                <SelectItem value="DEEPSEEK">DeepSeek</SelectItem>
                                                <SelectItem value="META">Meta</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FormField>

                                    <FormField
                                        label="LLM Model"
                                        error={errors.llm_model}
                                        description="Select the AI model"
                                    >
                                        <Select
                                            value={formData.llm_model || undefined}
                                            onValueChange={(value) => handleInputChange("llm_model", value)}
                                            key={`model-${formData.llm_provider}-${formData.llm_model || "empty"}`}
                                            disabled={isLoadingModels}
                                        >
                                            <SelectTrigger className={`border-border/40 ${errors.llm_model ? "border-destructive" : ""}`}>
                                                <SelectValue placeholder={isLoadingModels ? "Loading models..." : "Select a model"} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {filteredModels.map((model) => (
                                                    <SelectItem key={model.model_id} value={model.model_key}>
                                                        {model.display_name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormField>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
